import React, {useEffect, useState} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import {
    Badge,
    Box,
    IconButton,
    Image,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {SearchBar} from '../header'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {SearchIcon, AccountIcon, BasketIcon} from '../icons'
import useTagManager from '../../hooks/use-tag-manager'
import {useSiteContext} from '../../contexts/site'
import {getUrl} from '../../utils/cloudinary-utils'
import {getTotalQuantity} from '../../utils/basket-utils'
import {BASKET_URL} from '../../constants'

const StickyBackSection = ({...rest}) => {
    const {onLogoClick, setShowSpinner, onClick, onMyAccountClick} = rest
    const intl = useIntl()
    const {isYR} = useSiteContext()
    const styles = useMultiStyleConfig('Header')
    const tagManager = useTagManager()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    const {data: basket} = useCurrentBasket()
    var changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const [totalQuantity, setTotalQuantity] = useState(0)
    const quantityTotal = basket?.productItems?.reduce((s, i) => s + i?.quantity, 0)
    const giftQtyTotal = basket?.giftCertificateItems?.reduce((s, i) => s + i?.quantity, 0)
    useEffect(() => {
        setTotalQuantity(getTotalQuantity(basket, changeUpProductID))
    }, [quantityTotal, giftQtyTotal])
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [showSearchTab, setShowSearchTab] = useState(false)
    return (
        <Box className="sticky-back-section" display="flex">
            <Box className="left-section">
                <IconButton
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.logo',
                        defaultMessage: 'Logo'
                    })}
                    icon={
                        isYR ? (
                            <Image
                                {...styles.logo}
                                src={getUrl('UIImages/images/full-yr-logo.svg')}
                                width="171px"
                                alt="Yellow Rose Logo"
                                height="34px"
                                verticalAlign="middle"
                            />
                        ) : (
                            <Image
                                {...styles.logo}
                                src={getUrl('UIImages/images/full-ks-logo.svg')}
                                alt="Kendra Scott Logo"
                                width="193px"
                                height="33.19px"
                                verticalAlign="middle"
                            />
                        )
                    }
                    {...styles.icons.mobileBrandLogo}
                    onClick={onLogoClick}
                />
            </Box>
            <Box className="right-section" position="absolute" right="2.7rem" top="1rem">
                <Box
                    className="search-menu"
                    width={showSearchTab ? '100%' : '0'}
                    left={showSearchTab ? 0 : '-100%'}
                    {...styles.searchMenu}
                    sx={{
                        backdropFilter: showSearchTab ? {md: 'blur(5px)'} : 'none'
                    }}
                >
                    <SearchBar
                        setShowSearchTab={(bool) => {
                            setShowSearchTab(bool)
                        }}
                        id="sticky-back"
                        setShowSpinner={() => {
                            setShowSpinner(true)
                            onClick(false)
                        }}
                        showSearchTab={showSearchTab}
                        isStickyBackSection={true}
                    />
                </Box>
                <IconButton
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.search',
                        defaultMessage: 'Search'
                    })}
                    icon={
                        <SearchIcon
                            alt="Search Logo"
                            width="15px"
                            height="15px"
                            verticalAlign="middle"
                            color={isYR ? 'white' : 'black'}
                        />
                    }
                    {...styles.icons.main}
                    {...styles.icons.mobileSearchLogo}
                    margin="0"
                    onClick={() => {
                        tagManager.formFieldChange('simpleSearch', 'Form Submit Intent')
                        setShowSearchTab(!showSearchTab)
                    }}
                    minWidth="26.38px"
                    minHeight="28.38px"
                />
                <IconButton
                    icon={
                        <AccountIcon
                            alt="User Icon"
                            width="16px"
                            height="16px"
                            verticalAlign="middle"
                            color={isYR ? 'white' : 'black'}
                        />
                    }
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account',
                        defaultMessage: 'My Account'
                    })}
                    variant="unstyled"
                    display={{lg: 'none'}}
                    {...styles.icons.main}
                    margin="0"
                    minWidth="26.38px"
                    minHeight="28.38px"
                    onClick={onMyAccountClick}
                />
                <IconButton
                    as={RouteLink}
                    to={BASKET_URL}
                    sx={{
                        'z-index': 'auto'
                    }}
                    opacity="1"
                    pointerEvents="auto"
                    className="basket-icon"
                    aria-label={intl.formatMessage(
                        {
                            id: 'header.button.assistive_msg.my_cart_with_num_items',
                            defaultMessage: 'My cart, number of items: {numItems}'
                        },
                        {numItems: totalQuantity}
                    )}
                    icon={
                        <>
                            <BasketIcon
                                alt="Bag icon"
                                width={isDesktop ? '18px' : '14px'}
                                height={isDesktop ? '20px' : '16px'}
                                verticalAlign="middle"
                                color={{base: isYR ? 'white' : 'black', lg: 'black'}}
                            />
                            {basket && totalQuantity > 0 && (
                                <Badge
                                    variant="notification"
                                    className="total-items"
                                    fontSize="9px"
                                    color={isYR && !isDesktop ? 'black' : 'white'}
                                    backgroundColor={isYR && !isDesktop ? 'white' : 'black'}
                                    top="0.10rem"
                                    right="0.2rem"
                                >
                                    {totalQuantity}
                                </Badge>
                            )}
                        </>
                    }
                    variant="unstyled"
                    {...styles.icons.main}
                    margin="0"
                    padding="0.7rem"
                    minWidth="26.38px"
                    minHeight="28.38px"
                    display="inline-grid"
                />
            </Box>
        </Box>
    )
}

export default StickyBackSection
